import { useState } from 'react'
import { Link2, Calculator, HandCoins } from 'lucide-react'
import { formatOdds, formatProb, resolvePickBadgeStatus } from '@/lib/utils'
import { StatusBadge } from './StatusBadge'
import { parlayDisplayStatus } from '@/lib/badges'
import { RiskBadge } from './RiskBadge'
import { TeamCrest } from './TeamCrest'
import { RunBadge } from './RunBadge'
import { RegisterBetModal } from './RegisterBetModal'
import { PlayedBadge } from './PlayedBadge'
import type { Parlay } from '@/types/miniApp'

// P0.23 (22/08/2026, Sección 7 del encargo): identificado por `parlay_type` REAL, nunca por
// cantidad de piernas -- bug real corregido: el lookup anterior por `legCount` (2/3/5) no
// cubría Same Game Parlay (4-7 piernas), así que si un SGP llegaba a esta card (nunca debería,
// ver `ParlaysPage.tsx`) mostraba un título crudo sin traducir.
// Sprint "Parlay Official Final" (25/09/2026): renombrado de producto a JA Duo/Triple/Quad --
// probability-first, ya sin semántica de riesgo Conservador/Balanceado/Agresivo (confirmado
// por auditoría de `parlay_optimizer.py`: las 3 categorías comparten la MISMA función objetivo
// lexicográfica desde antes de este sprint; el único diferenciador real es el nº de piernas
// exacto -- 2/3/4 -- y el piso de cuota combinada mínima). `parlay_type` interno
// ('conservador'/'balanceado'/'agresivo') se mantiene sin cambios por continuidad de datos
// históricos (Parlay.parlay_type, StrategyPerformance, PersonalBetCategory) -- solo cambia el
// texto mostrado al usuario.
const PARLAY_CONFIG: Record<string, { title: string; subtitle: string; color: string; badgeBg: string }> = {
  conservador: {
    title: 'JA Duo', subtitle: 'Dos selecciones, máxima probabilidad conjunta real.',
    color: 'text-emerald-400', badgeBg: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  },
  balanceado: {
    title: 'JA Triple', subtitle: 'Tres selecciones, máxima probabilidad conjunta real.',
    color: 'text-cyan-400', badgeBg: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
  },
  agresivo: {
    title: 'JA Quad', subtitle: 'Cuatro selecciones, máxima probabilidad conjunta real.',
    color: 'text-amber-400', badgeBg: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  },
}

export function ParlayCard({ parlay }: { parlay: Parlay }) {
  const sortedLegs = parlay.legs.slice().sort((a, b) => a.leg_order - b.leg_order)
  const legCount = sortedLegs.length

  const config = PARLAY_CONFIG[parlay.parlay_type] ?? {
    title: `Parlay ${parlay.parlay_type || 'Especial'}`,
    subtitle: '',
    color: 'text-accent',
    badgeBg: 'bg-accent/20 text-accent border-accent/40',
  }

  // Simulador dinámico con slider -- Fase R.12 (Bloque C): esto es SOLO una calculadora
  // visual, nunca llama a la API ni afecta Mi Banca Personal. La única acción que registra
  // una apuesta real es "LA JUGUÉ" más abajo.
  const [stake, setStake] = useState<number>(15)
  const [showPlayModal, setShowPlayModal] = useState(false)

  const combinedOdds = parlay.combined_odds || 1
  const returnValue = stake * combinedOdds
  const netProfit = returnValue - stake

  return (
    <div className="card space-y-4 border-parlay/40 bg-gradient-to-b from-panel-elevated/40 to-panel/90 hover:border-parlay/60 transition duration-300 shadow-lg rounded-2xl">
      
      {/* Cabecera Parlay 2.0 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-2xl bg-parlay-soft flex items-center justify-center border border-parlay/30 shadow-sm shrink-0">
            <Link2 className="h-4 w-4 text-parlay" />
          </div>
          <div>
            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black font-mono border uppercase ${config.badgeBg}`}>
              {config.title}
            </span>
            <p className="text-[10px] text-text-dim font-bold uppercase mt-1">
              {legCount} selecciones combinadas
            </p>
            {config.subtitle && <p className="text-[10px] text-text-muted mt-0.5">{config.subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {parlay.run_type && <RunBadge runType={parlay.run_type} compact />}
          <StatusBadge status={parlayDisplayStatus(parlay.status, parlay.is_bettable)} />
        </div>
      </div>

      {/* Listado de Apuestas (Legs) con Escudos Grandes (36px) -- Fase R.8 (16/08/2026,
          sección 8): más separación vertical entre piernas y layout en columna en vez de
          fila comprimida, para que el texto (partido/selección) nunca quede pegado ni
          solapado con la cuota, sin importar el largo del nombre del equipo. */}
      <div className="relative space-y-5 pl-4 ml-1 border-l-2 border-parlay/30 py-1.5">
        {sortedLegs.map((leg) => {
          // Sección D (25/09/2026, "State Consistency"): esta card leía `leg.settled_status`
          // crudo sin pasar por `resolvePickBadgeStatus` -- MISMA lectura canónica que
          // TopPickCard/SecondaryPickCard/PickDetailSheet (P1.10). El bypass no producía el
          // síntoma "Falló prematuro" reportado (settled_status es null antes de liquidar, el
          // `&&` simplemente ocultaba el badge), pero sí dejaba a las piernas de Parlay sin
          // badge "EN JUEGO"/"PENDIENTE" -- inconsistente con el resto de la app.
          const legBadgeStatus = resolvePickBadgeStatus(
            leg.fixture_status as string | null | undefined,
            leg.settled_status
          )
          return (
          <div key={leg.leg_order} className="relative flex flex-col gap-2 text-xs">
            {/* Indicador de nodo */}
            <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-parlay border-2 border-black shadow-glow-accent" />

            <div className="flex items-center gap-2.5 min-w-0">
              {/* ESCUDOS GRANDES (36PX) */}
              <div className="flex items-center gap-1.5 shrink-0 bg-panel-elevated p-1 rounded-xl border border-border">
                <TeamCrest src={leg.home_team_logo} alt={(leg.home_team as string) ?? 'Local'} size={36} />
                <span className="text-[10px] font-bold text-text-dim">VS</span>
                <TeamCrest src={leg.away_team_logo} alt={(leg.away_team as string) ?? 'Visitante'} size={36} />
              </div>
              <span className="text-xs font-black text-text leading-snug break-words min-w-0">
                {(leg.match_label as string) ?? `Partido ${leg.leg_order}`}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] font-black text-emerald-400 uppercase tracking-wide leading-snug break-words min-w-0">
                {((leg.selection_label ?? leg.selection) as string | undefined)?.toString().toUpperCase()}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                {legBadgeStatus && <StatusBadge status={legBadgeStatus} />}
                <span className="text-xs font-mono font-black text-text tabular-nums bg-panel-elevated px-2.5 py-1 rounded-xl border border-border shadow-sm">
                  @{formatOdds(leg.odds as number)}
                </span>
              </div>
            </div>

            {/* P0.32 (26/08/2026, rediseño Mini App -- Sección 3 del encargo): resultado
                real de ESTA pierna cuando ya liquidó -- nunca solo ACIERTO/FALLO. */}
            {leg.settled_status && (leg.market_result_display || leg.stat_result_display) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {leg.market_result_display && (
                  <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                    {leg.market_result_display}
                  </span>
                )}
                {leg.stat_result_display && (
                  <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                    {leg.stat_result_display}
                  </span>
                )}
              </div>
            )}
          </div>
          )
        })}
      </div>

      {/* Grid del Resumen (Cuota, EV, Riesgo) */}
      <div className="grid grid-cols-3 gap-2.5 pt-3.5 border-t border-border/40 text-center">
        <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/40">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Cuota total</p>
          <p className="text-base font-black text-emerald-400 font-mono tabular-nums mt-0.5">@{formatOdds(parlay.combined_odds)}</p>
        </div>
        <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/40">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Valor (EV)</p>
          <p className="text-base font-black text-emerald-400 font-mono tabular-nums mt-0.5">+{formatProb(parlay.expected_value)}</p>
        </div>
        <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/40 flex flex-col items-center justify-center">
          <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim mb-0.5">Riesgo</p>
          {parlay.correlation_level ? (
            <RiskBadge level={parlay.correlation_level} />
          ) : (
            <span className="text-xs font-black text-text">—</span>
          )}
        </div>
      </div>

      {/* Simulador Interactivo de Parlay */}
      <div className="pt-3.5 border-t border-border/40 space-y-3">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5">
            <Calculator className="h-3.5 w-3.5 text-text-dim" />
            <span className="text-text-dim font-bold text-[10px] uppercase tracking-wider">Simulador de apuesta</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-text-dim font-bold">IMPORTE:</span>
            <span className="font-mono font-black text-text bg-panel px-2 py-0.5 rounded border border-border">{stake} €</span>
          </div>
        </div>

        <input
          type="range"
          min={5}
          max={100}
          step={5}
          value={stake}
          onChange={(e) => setStake(Number(e.target.value))}
          className="w-full h-1.5 bg-panel-elevated rounded-lg appearance-none cursor-pointer accent-parlay"
        />

        <div className="grid grid-cols-2 gap-2 bg-panel/80 p-3 rounded-xl border border-border/60 text-xs">
          <div>
            <span className="text-[9px] text-text-dim uppercase font-bold block">RETORNO POTENCIAL</span>
            <span className="font-mono font-black text-text text-sm">{returnValue.toFixed(2)} €</span>
          </div>
          <div className="text-right">
            <span className="text-[9px] text-emerald-400 uppercase font-bold block">BENEFICIO NETO</span>
            <span className="font-mono font-black text-emerald-400 text-sm">+{netProfit.toFixed(2)} €</span>
          </div>
        </div>
      </div>

      {/* Fase R.12 (Bloque C, 17/08/2026): "LA JUGUÉ" -- separado visualmente del simulador
          de arriba (que nunca afecta la banca). Esta es la ÚNICA acción que registra el
          Parlay COMPLETO como apuesta real en Mi Banca Personal.
          Fase R.13 (17/08/2026): se permite registrar en cualquier estado real del Parlay
          (`valido`/`ganada`/`perdida`/`void`) -- el usuario puede estar registrando tarde una
          apuesta que ya hizo por su cuenta antes de que las piernas empezaran o terminaran.
          Solo `no_formado` queda excluido: ahí no existe una combinación real que jugar. */}
      {parlay.status !== 'no_formado' && (
        parlay.my_bet ? (
          <PlayedBadge bet={parlay.my_bet} />
        ) : (
          <button
            onClick={() => setShowPlayModal(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-panel-elevated border border-parlay/40 text-parlay hover:bg-parlay-soft/20 text-[10px] font-black uppercase tracking-wider transition"
          >
            <HandCoins className="h-3.5 w-3.5" />
            La jugué
          </button>
        )
      )}

      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          matchLabel={`${config.title} · ${legCount} selecciones`}
          selectionLabel={sortedLegs.map((leg) => (leg.selection_label ?? leg.selection ?? '') as string).join(' + ')}
          referenceOdds={parlay.combined_odds ?? 0}
          parlayId={parlay.id}
        />
      )}
    </div>
  )
}
